'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import {
  downloadCSV,
  emailReport,
  buildBookingReport,
  buildLeadReport,
  generateHTMLReport,
} from '@/lib/emailReportExport';

export default function EmailReportExport() {
  const [email, setEmail] = useState('');
  const [reportType, setReportType] = useState<'bookings' | 'leads'>('bookings');
  const [exportFormat, setExportFormat] = useState<'csv' | 'html'>('csv');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const getReportData = () => {
    return reportType === 'bookings' ? buildBookingReport() : buildLeadReport();
  };

  const handleDownload = () => {
    const data = getReportData();
    if (exportFormat === 'csv') {
      downloadCSV(data);
      setStatus('success');
      setMessage(`${data.title} downloaded as CSV`);
    } else {
      const html = generateHTMLReport(data);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setStatus('success');
      setMessage(`${data.title} downloaded as HTML`);
    }
    setTimeout(() => setStatus('idle'), 3000);
  };

  const handleEmailSend = () => {
    if (!email) {
      setStatus('error');
      setMessage('Please enter an email address');
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }
    const data = getReportData();
    emailReport(data, email);
    setStatus('success');
    setMessage('Email client opened with report details');
    setTimeout(() => setStatus('idle'), 3000);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
          <Icon name="EnvelopeIcon" size={22} className="text-primary" />
        </div>
        <div>
          <h3 className="font-heading font-black text-lg text-foreground">Export Reports</h3>
          <p className="text-sm text-muted-foreground">Download or email analytics reports</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Report Type */}
        <div>
          <label className="block text-sm font-bold text-foreground mb-2">Report Type</label>
          <div className="grid grid-cols-2 gap-3">
            {(['bookings', 'leads'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setReportType(type)}
                className={`p-3 border-2 rounded-xl text-sm font-bold capitalize transition-all ${
                  reportType === type ? 'border-primary bg-primary/10 text-primary' : 'border-border text-foreground hover:border-primary/50'
                }`}
              >
                <Icon name={type === 'bookings' ? 'TicketIcon' : 'UserGroupIcon'} size={18} className="mx-auto mb-1" />
                {type === 'bookings' ? 'Booking Analytics' : 'Lead Generation'}
              </button>
            ))}
          </div>
        </div>

        {/* Export Format */}
        <div>
          <label className="block text-sm font-bold text-foreground mb-2">Export Format</label>
          <div className="grid grid-cols-2 gap-3">
            {(['csv', 'html'] as const).map((fmt) => (
              <button
                key={fmt}
                onClick={() => setExportFormat(fmt)}
                className={`p-3 border-2 rounded-xl text-sm font-bold uppercase transition-all ${
                  exportFormat === fmt ? 'border-primary bg-primary/10 text-primary' : 'border-border text-foreground hover:border-primary/50'
                }`}
              >
                {fmt === 'csv' ? '📊 CSV Spreadsheet' : '🌐 HTML Report'}
              </button>
            ))}
          </div>
        </div>

        {/* Download Button */}
        <button
          onClick={handleDownload}
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-secondary text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all"
        >
          <Icon name="ArrowDownTrayIcon" size={20} />
          Download Report
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-xs text-muted-foreground font-bold">OR EMAIL DIRECTLY</span>
          </div>
        </div>

        {/* Email Input */}
        <div>
          <label className="block text-sm font-bold text-foreground mb-2">Email Address</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter recipient email..."
            className="w-full px-4 py-3 border-2 border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
        </div>

        <button
          onClick={handleEmailSend}
          className="w-full flex items-center justify-center gap-2 border-2 border-primary text-primary font-bold py-3 rounded-xl hover:bg-primary hover:text-white transition-all"
        >
          <Icon name="PaperAirplaneIcon" size={20} />
          Send via Email
        </button>

        {/* Status Message */}
        {status !== 'idle' && (
          <div className={`flex items-center gap-2 p-3 rounded-xl text-sm font-bold ${
            status === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            <Icon name={status === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={18} />
            {message}
          </div>
        )}
      </div>
    </div>
  );
}
